import React from 'react'
import { useWhatsAppStatus } from '../../contexts/WhatsAppStatusContext'

/**
 * Prominent status card for the Integrations page. Always visible (even when
 * connected — it's the page admins go to specifically to inspect integration
 * health) and colour-coded by current state.
 *
 * Props:
 *   - onConfigure  callback to open the WhatsApp setup modal (admins use this
 *                  to configure or scan a fresh QR when disconnected)
 */
export interface WhatsAppStatusCardProps {
  onConfigure: () => void
}

export function WhatsAppStatusCard({ onConfigure }: WhatsAppStatusCardProps) {
  const status = useWhatsAppStatus()

  // Three visual variants to match the three-state model in the context.
  const variant =
    status.state === 'connected'
      ? {
          ring: 'border-success/30 bg-success/5',
          dot: 'bg-success',
          label: 'text-success',
          title: 'WhatsApp is connected',
          detail: status.activeSession?.id
            ? `Session ${status.activeSession.id} · status ${status.activeSession.status || 'ready'}`
            : 'Linked and receiving messages',
        }
      : status.state === 'disconnected'
      ? {
          ring: 'border-error/30 bg-error/5',
          dot: 'bg-error',
          label: 'text-error',
          title: 'WhatsApp is disconnected',
          detail:
            status.error
              ? `Could not reach the OpenWA gateway (${status.error}). Cached group data is still available.`
              : status.connected
                ? 'OpenWA is reachable but no authenticated WhatsApp session. Scan a fresh QR to reconnect.'
                : 'The OpenWA gateway is not responding. New messages will be missed until you reconnect.',
        }
      : status.state === 'not_configured'
      ? {
          ring: 'border-warning/30 bg-warning/5',
          dot: 'bg-warning',
          label: 'text-warning',
          title: 'WhatsApp is not configured',
          detail: 'Link a WhatsApp account to start receiving customer messages.',
        }
      : {
          ring: 'border-border bg-surface-secondary',
          dot: 'bg-txt-tertiary',
          label: 'text-txt-secondary',
          title: 'Checking WhatsApp status…',
          detail: '',
        }

  const lastSynced =
    status.last_synced_at
      ? `Cached groups last synced ${new Date(status.last_synced_at).toLocaleString()} (${status.cached_group_count} groups)`
      : 'No cached groups yet'

  return (
    <div className={`mb-6 rounded-xl border ${variant.ring} px-5 py-4 flex items-start gap-4`}>
      <span
        className={`mt-1 inline-flex w-3 h-3 rounded-full ${variant.dot} ${status.state === 'disconnected' ? 'animate-pulse' : ''} shrink-0`}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase tracking-wider ${variant.label}`}>
            WhatsApp
          </span>
          <span className="text-sm font-semibold text-txt-primary">{variant.title}</span>
        </div>
        {variant.detail && (
          <p className="text-sm text-txt-secondary mt-1">{variant.detail}</p>
        )}
        <p className="text-xs text-txt-tertiary mt-2">{lastSynced}</p>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        <button
          onClick={onConfigure}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition-colors shadow-sm"
        >
          {status.state === 'not_configured' ? 'Configure' : status.state === 'disconnected' ? 'Reconnect' : 'Manage'}
        </button>
        <button
          onClick={status.refresh}
          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs text-txt-secondary hover:text-brand-600 hover:bg-surface-hover transition-colors"
          title="Re-check status now"
        >
          ↻ Refresh
        </button>
      </div>
    </div>
  )
}
